import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { addEdge, Background, Controls, MiniMap, Panel, ReactFlow, ReactFlowProvider, useEdgesState, useNodesState, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './editor.css';
import TextNode from './TextNode.jsx';

const NODE_TYPES = { gmText: TextNode };

function uid() {
  return typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createDefaultNode() {
  return { id: uid(), type: 'gmText', position: { x: 80, y: 80 }, data: { title: 'Início', body: 'Descreva o primeiro passo do guia.' } };
}

function hydrateNode(node) {
  return { id: node.id || uid(), type: node.type || 'gmText', position: { x: Number(node.position?.x) || 0, y: Number(node.position?.y) || 0 }, data: { title: node.data?.title || '', body: node.data?.body || '' } };
}

function hydrateNodes(nodes) {
  return Array.isArray(nodes) && nodes.length ? nodes.map(hydrateNode) : [createDefaultNode()];
}

function hydrateEdges(edges) {
  return Array.isArray(edges) ? edges.map((edge) => ({ id: edge.id || uid(), source: edge.source, target: edge.target, sourceHandle: edge.sourceHandle || null, targetHandle: edge.targetHandle || null })) : [];
}

function serializeNodes(nodes) {
  return nodes.map((node) => ({ id: node.id, type: node.type, position: node.position, data: { title: node.data?.title || '', body: node.data?.body || '' } }));
}

function serializeEdges(edges) {
  return edges.map((edge) => ({ id: edge.id, source: edge.source, target: edge.target, sourceHandle: edge.sourceHandle || null, targetHandle: edge.targetHandle || null }));
}

function FlowCanvas({ mode, bridgeRef, onBridgeReady }) {
  const readOnly = mode === 'view';
  const [nodes, setNodes, onNodesChange] = useNodesState(() => [createDefaultNode()]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { screenToFlowPosition, fitView } = useReactFlow();
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);

  const handleChange = useCallback((id, patch) => {
    setNodes((current) => current.map((node) => (node.id === id ? { ...node, data: { ...node.data, ...patch } } : node)));
  }, [setNodes]);

  const handleDelete = useCallback((id) => {
    setNodes((current) => current.filter((node) => node.id !== id));
    setEdges((current) => current.filter((edge) => edge.source !== id && edge.target !== id));
  }, [setNodes, setEdges]);

  const onConnect = useCallback((connection) => {
    setEdges((current) => addEdge({ ...connection, id: `e-${uid()}` }, current));
  }, [setEdges]);

  const addNode = useCallback(() => {
    const position = screenToFlowPosition({ x: window.innerWidth / 2, y: 220 });
    setNodes((current) => [...current, { id: uid(), type: 'gmText', position, data: { title: 'Novo passo', body: '' } }]);
  }, [screenToFlowPosition, setNodes]);

  useEffect(() => {
    if (!bridgeRef) return;
    bridgeRef.current = {
      getData: () => ({ nodes: serializeNodes(nodesRef.current), edges: serializeEdges(edgesRef.current) }),
      setData: (data) => {
        setNodes(hydrateNodes(data?.nodes));
        setEdges(hydrateEdges(data?.edges));
        requestAnimationFrame(() => fitView({ padding: 0.2 }));
      }
    };
    onBridgeReady?.();
  }, [bridgeRef, onBridgeReady, setNodes, setEdges, fitView]);

  const displayNodes = useMemo(
    () => nodes.map((node) => ({ ...node, data: { ...node.data, onChange: handleChange, onDelete: handleDelete, readOnly } })),
    [nodes, handleChange, handleDelete, readOnly]
  );

  return (
    <ReactFlow
      nodes={displayNodes}
      edges={edges}
      nodeTypes={NODE_TYPES}
      onNodesChange={readOnly ? undefined : onNodesChange}
      onEdgesChange={readOnly ? undefined : onEdgesChange}
      onConnect={readOnly ? undefined : onConnect}
      nodesDraggable={!readOnly}
      nodesConnectable={!readOnly}
      elementsSelectable={!readOnly}
      deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
      fitView
      minZoom={0.15}
      maxZoom={2}
    >
      <Background gap={18} size={1} color="rgba(151,220,202,0.14)" />
      <Controls showInteractive={!readOnly} />
      <MiniMap pannable zoomable style={{ background: '#09111d' }} maskColor="rgba(5,13,12,0.65)" />
      {!readOnly && (
        <Panel position="top-left" className="gm-flow-panel">
          <button type="button" onClick={addNode}>+ Adicionar nó</button>
        </Panel>
      )}
    </ReactFlow>
  );
}

function mount() {
  const container = document.getElementById('guiaMarniCanvas');
  if (!container) return;
  const mode = container.dataset.mode === 'view' ? 'view' : 'edit';
  const bridgeRef = { current: null };
  let resolveReady;
  const readyPromise = new Promise((resolve) => { resolveReady = resolve; });
  const root = createRoot(container);
  root.render(
    <ReactFlowProvider>
      <FlowCanvas mode={mode} bridgeRef={bridgeRef} onBridgeReady={() => resolveReady()} />
    </ReactFlowProvider>
  );
  window.GuiaMarniFlow = {
    ready: readyPromise,
    getData: () => bridgeRef.current?.getData() || { nodes: [], edges: [] },
    setData: (data) => bridgeRef.current?.setData(data)
  };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
else mount();
